import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Typography,
  Card,
  Alert,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Modal,
  ModalDialog,
  ModalClose
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import MaterialForm, {
  emptyMaterialForm,
  materialFormToPayload,
  validateMaterialForm
} from '../components/MaterialForm';
import DisposableForm, {
  emptyDisposableForm,
  disposableFormToPayload,
  validateDisposableForm
} from '../components/DisposableForm';
import ImplantForm, {
  emptyImplantForm,
  implantFormToPayload,
  validateImplantForm
} from '../components/ImplantForm';
import InstrumentForm, {
  emptyInstrumentForm,
  instrumentFormToPayload,
  validateInstrumentForm
} from '../components/InstrumentForm';
import EquipmentForm, {
  emptyEquipmentForm,
  equipmentFormToPayload,
  validateEquipmentForm
} from '../components/EquipmentForm';

const ADD_ITEM_TABS = [
  { value: 0, label: 'Material' },
  { value: 1, label: 'Disposable' },
  { value: 2, label: 'Implant' },
  { value: 3, label: 'Instrument' },
  { value: 4, label: 'Equipment' }
];

const tabListSx = {
  flexWrap: 'nowrap',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: 0.5,
  px: 1,
  pt: 1,
  mx: 0,
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { height: 4 },
  '& .MuiTab-root': {
    flex: '0 0 auto',
    minWidth: 'max-content',
    whiteSpace: 'nowrap',
    fontSize: { xs: '0.8125rem', sm: '0.875rem' },
    px: { xs: 1.25, sm: 1.75 },
    py: 1
  }
};

const AddItem = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [addVendorLoading, setAddVendorLoading] = useState(false);
  const [addVendorError, setAddVendorError] = useState('');
  const [newVendor, setNewVendor] = useState({
    name: '',
    mobile: '',
    email: '',
    address: ''
  });

  const [materialForm, setMaterialForm] = useState(emptyMaterialForm);
  const [disposableForm, setDisposableForm] = useState(emptyDisposableForm);
  const [implantForm, setImplantForm] = useState(emptyImplantForm);
  const [instrumentForm, setInstrumentForm] = useState(emptyInstrumentForm);
  const [equipmentForm, setEquipmentForm] = useState(emptyEquipmentForm);

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      setVendorsLoading(true);
      const response = await axiosInstance.get('/vendors');
      setVendors(response.data.vendors || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load vendors');
    } finally {
      setVendorsLoading(false);
    }
  };

  const handleMaterialChange = (e) => {
    const { name, value } = e.target || e;
    if (name === 'vendor_id' && value === '__add_new__') {
      setAddVendorOpen(true);
      return;
    }
    setMaterialForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDisposableChange = (e) => {
    const { name, value } = e.target || e;
    if (name === 'vendor_id' && value === '__add_new__') {
      setAddVendorOpen(true);
      return;
    }
    setDisposableForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImplantChange = (e) => {
    const { name, value } = e.target || e;
    if (name === 'vendor_id' && value === '__add_new__') {
      setAddVendorOpen(true);
      return;
    }
    setImplantForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInstrumentChange = (e) => {
    const { name, value } = e.target || e;
    if (name === 'vendor_id' && value === '__add_new__') {
      setAddVendorOpen(true);
      return;
    }
    setInstrumentForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEquipmentChange = (e) => {
    const { name, value } = e.target || e;
    if (name === 'vendor_id' && value === '__add_new__') {
      setAddVendorOpen(true);
      return;
    }
    setEquipmentForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNewVendorChange = (e) => {
    setNewVendor((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleAddVendor = async (e) => {
    e.preventDefault();
    setAddVendorError('');
    if (!newVendor.name.trim()) {
      setAddVendorError('Vendor name is required');
      return;
    }
    try {
      setAddVendorLoading(true);
      const response = await axiosInstance.post('/vendors', {
        name: newVendor.name.trim(),
        mobile: newVendor.mobile.trim() || undefined,
        email: newVendor.email.trim() || undefined,
        address: newVendor.address.trim() || undefined
      });
      const created = response.data.vendor;
      setVendors((prev) => [...prev, created]);
      setMaterialForm((prev) => ({ ...prev, vendor_id: String(created.id) }));
      setDisposableForm((prev) => ({ ...prev, vendor_id: String(created.id) }));
      setImplantForm((prev) => ({ ...prev, vendor_id: String(created.id) }));
      setInstrumentForm((prev) => ({ ...prev, vendor_id: String(created.id) }));
      setEquipmentForm((prev) => ({ ...prev, vendor_id: String(created.id) }));
      setNewVendor({ name: '', mobile: '', email: '', address: '' });
      setAddVendorOpen(false);
    } catch (err) {
      setAddVendorError(err.response?.data?.error || 'Failed to add vendor');
    } finally {
      setAddVendorLoading(false);
    }
  };

  const runValidation = (message) => {
    if (message) {
      setError(message);
      return false;
    }
    return true;
  };

  const handleMaterialSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!runValidation(validateMaterialForm(materialForm))) return;
    try {
      setLoading(true);
      await axiosInstance.post('/materials', materialFormToPayload(materialForm));
      setSuccess('Material added successfully!');
      setMaterialForm(emptyMaterialForm());
      setTimeout(() => navigate('/stock'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add material');
    } finally {
      setLoading(false);
    }
  };

  const handleDisposableSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!runValidation(validateDisposableForm(disposableForm))) return;
    try {
      setLoading(true);
      await axiosInstance.post('/disposables', disposableFormToPayload(disposableForm));
      setSuccess('Disposable added successfully!');
      setDisposableForm(emptyDisposableForm());
      setTimeout(() => navigate('/disposables'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add disposable');
    } finally {
      setLoading(false);
    }
  };

  const handleInstrumentSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!runValidation(validateInstrumentForm(instrumentForm))) return;
    try {
      setLoading(true);
      await axiosInstance.post('/instruments', instrumentFormToPayload(instrumentForm));
      setSuccess('Instrument added successfully!');
      setInstrumentForm(emptyInstrumentForm());
      setTimeout(() => navigate('/instruments'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add instrument');
    } finally {
      setLoading(false);
    }
  };

  const handleEquipmentSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!runValidation(validateEquipmentForm(equipmentForm))) return;
    try {
      setLoading(true);
      await axiosInstance.post('/equipment', equipmentFormToPayload(equipmentForm));
      setSuccess('Equipment added successfully!');
      setEquipmentForm(emptyEquipmentForm());
      setTimeout(() => navigate('/equipment'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add equipment');
    } finally {
      setLoading(false);
    }
  };

  const handleImplantSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!runValidation(validateImplantForm(implantForm))) return;
    try {
      setLoading(true);
      await axiosInstance.post('/implants', implantFormToPayload(implantForm));
      setSuccess('Implant added successfully!');
      setImplantForm(emptyImplantForm());
      setTimeout(() => navigate('/implants'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add implant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        px: { xs: 1.5, md: 3 },
        pt: { xs: 1.5, md: 3 },
        pb: { xs: 2, md: 3 }
      }}
    >
      {error && (
        <Alert color="danger" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Card sx={{ maxWidth: 800, mx: 'auto', width: '100%', flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
          <TabList sx={tabListSx}>
            {ADD_ITEM_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value}>
                {tab.label}
              </Tab>
            ))}
          </TabList>

          <TabPanel value={0} sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
            <MaterialForm
              form={materialForm}
              onChange={handleMaterialChange}
              onSubmit={handleMaterialSubmit}
              loading={loading}
              vendors={vendors}
              vendorsLoading={vendorsLoading}
              onAddVendor={() => setAddVendorOpen(true)}
            />
          </TabPanel>

          <TabPanel value={1} sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
            <DisposableForm
              form={disposableForm}
              onChange={handleDisposableChange}
              onSubmit={handleDisposableSubmit}
              loading={loading}
              vendors={vendors}
              vendorsLoading={vendorsLoading}
              onAddVendor={() => setAddVendorOpen(true)}
            />
          </TabPanel>

          <TabPanel value={2} sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
            <ImplantForm
              form={implantForm}
              onChange={handleImplantChange}
              onSubmit={handleImplantSubmit}
              loading={loading}
              vendors={vendors}
              vendorsLoading={vendorsLoading}
              onAddVendor={() => setAddVendorOpen(true)}
            />
          </TabPanel>

          <TabPanel value={3} sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
            <InstrumentForm
              form={instrumentForm}
              onChange={handleInstrumentChange}
              onSubmit={handleInstrumentSubmit}
              loading={loading}
              vendors={vendors}
              vendorsLoading={vendorsLoading}
              onAddVendor={() => setAddVendorOpen(true)}
            />
          </TabPanel>

          <TabPanel value={4} sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
            <EquipmentForm
              form={equipmentForm}
              onChange={handleEquipmentChange}
              onSubmit={handleEquipmentSubmit}
              loading={loading}
              vendors={vendors}
              vendorsLoading={vendorsLoading}
              onAddVendor={() => setAddVendorOpen(true)}
            />
          </TabPanel>
        </Tabs>
      </Card>

      <Modal open={addVendorOpen} onClose={() => setAddVendorOpen(false)}>
        <ModalDialog sx={{ maxWidth: 400 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2, color: 'primary.500' }}>
            Add Vendor
          </Typography>
          {addVendorError && (
            <Alert color="danger" sx={{ mb: 2 }} onClose={() => setAddVendorError('')}>
              {addVendorError}
            </Alert>
          )}
          <form onSubmit={handleAddVendor}>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                value={newVendor.name}
                onChange={handleNewVendorChange}
                required
                placeholder="Vendor name"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Mobile</FormLabel>
              <Input
                name="mobile"
                value={newVendor.mobile}
                onChange={handleNewVendorChange}
                placeholder="Mobile number"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={newVendor.email}
                onChange={handleNewVendorChange}
                placeholder="Email"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Address</FormLabel>
              <Input
                name="address"
                value={newVendor.address}
                onChange={handleNewVendorChange}
                placeholder="Address"
                sx={{
                  '&:hover': { borderColor: 'primary.500' },
                  '&:focus-within': { borderColor: 'primary.500' }
                }}
              />
            </FormControl>
            <Button
              type="submit"
              loading={addVendorLoading}
              fullWidth
              sx={{
                backgroundColor: 'primary.500',
                '&:hover': { backgroundColor: 'primary.600' }
              }}
            >
              Add Vendor
            </Button>
          </form>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default AddItem;
